import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import * as repository from "@/data/authRepository";
import { api, isMockApi } from "@/lib/api";
import { onSessionExpired, serverAuth } from "@/lib/authSession";

const AdminAuthContext = createContext(null);
const normaliseAdmin = (admin) => admin?.role === "superadmin" ? { ...admin, role: "super_admin" } : admin;

export function AdminAuthProvider({ children }) {
  const { getAdminByEmail, getAdminById, getAdminSession, setAdminSession, clearAdminSession, seedIfNeeded } = repository;
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    async function restore() {
      const s = getAdminSession();
      if (isMockApi) {
        seedIfNeeded();
        if (s?.adminId) {
          const a = getAdminById(s.adminId);
          if (a?.isActive === false) clearAdminSession();
          else if (a) setAdmin(normaliseAdmin(a));
          else clearAdminSession();
        }
      } else if (s?.token) {
        try {
          const current = await serverAuth.restoreAdmin();
          if (!current || !['admin', 'super_admin'].includes(current.role)) throw new Error("Not an administrator");
          if (active) setAdmin(current);
        } catch {
          clearAdminSession();
        }
      }
      if (active) setLoading(false);
    }
    restore();
    return () => { active = false; };
  }, []);

  /** The admin session is retired by a password reset just as a vendor's is. */
  useEffect(() => {
    if (isMockApi) return undefined;
    return onSessionExpired("admin", () => {
      setAdmin(null);
      toast.info("Your password was changed. Please sign in again.");
    });
  }, []);

  const login = useCallback(async (email, password) => {
    if (!isMockApi) {
      const result = await api.loginAdmin({ email, password });
      if (!['admin', 'super_admin'].includes(result.user?.role)) throw new Error("This account is not a platform administrator.");
      setAdminSession({ token: result.token, expiresIn: result.expiresIn, email: result.user.email, adminId: result.user.id });
      setAdmin(result.user);
      return result.user;
    }
    const a = getAdminByEmail(email);
    if (!a) throw new Error("No admin account found with that email.");
    if (a.password !== password) throw new Error("Incorrect password.");
    setAdminSession({ email: a.email, adminId: a.id });
    const current = normaliseAdmin(a);
    setAdmin(current);
    return current;
  }, []);

  const logout = useCallback(async () => {
    if (!isMockApi) {
      try { await api.logoutAdmin(); } catch { /* Expired tokens are cleared locally. */ }
    }
    clearAdminSession();
    setAdmin(null);
  }, []);

  return (
    <AdminAuthContext.Provider value={{ admin, loading, login, logout }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used inside AdminAuthProvider");
  return ctx;
}
